import { authApi } from './auth'
import { EVENT, trackCheckout } from '../utils/track'

// A press of Unlock while signed out used to end at /login and stop there.
//
// The instrumentation that shipped on 29 Aug caught it happening: 31 Aug 09:16,
// one visitor, `cta_checkout_blocked`, reason `signed_out`, on a $299 course.
// The same person had subscribed to the newsletter four hours earlier from the
// same browser, so they were not a passer-by. They never created an account. It
// is the first checkout intent from a stranger the site has ever been able to
// see, and it did not survive the wall.
//
// Two things were wrong, and only one of them was the wall. The redirect pointed
// back at the course PAGE, so even somebody who did sign up landed on a wall of
// marketing copy and had to find the button a second time, with the decision
// already made and the momentum gone.
//
// So the intent travels with them. `?checkout=<productSlug>` is carried through
// signup and login, and the moment authentication succeeds it goes straight to
// Stripe rather than to a page with a button on it.

export const CHECKOUT_PARAM = 'checkout'

/**
 * Product slugs, as the backend defines them: lowercase, digits, hyphens.
 *
 * This value arrives from a query string, which is to say from anywhere, and is
 * POSTed straight to the billing API. Validating the shape here means a crafted
 * link cannot make the site send arbitrary strings to checkout, and a mangled
 * one degrades to "no intent" rather than to an error page.
 */
const SLUG = /^[a-z0-9][a-z0-9-]{0,80}$/

/**
 * A redirect target that cannot leave the site.
 *
 * `//evil.example` is a protocol-relative URL: handed to navigate() it looks
 * like a path and behaves like another origin. So the rule is a single leading
 * slash and nothing else, and anything failing it falls back rather than being
 * partially repaired.
 */
export function safeRedirect(raw: string | null, fallback = '/account'): string {
  if (!raw) return fallback
  if (!raw.startsWith('/')) return fallback
  if (raw.startsWith('//')) return fallback
  // A backslash is treated as a slash by some browsers when resolving a URL,
  // which makes /\evil.example a second way to write the same escape.
  if (raw.startsWith('/\\')) return fallback
  return raw
}

/** The product this visitor was trying to buy, or null when there is no intent. */
export function readCheckoutIntent(params: URLSearchParams): string | null {
  const slug = params.get(CHECKOUT_PARAM)
  return slug && SLUG.test(slug) ? slug : null
}

/**
 * Where a signed-out buyer should be sent, with their intent attached.
 *
 * Signup rather than login, deliberately. A visitor who is signed out and
 * pressing Unlock is more likely never to have had an account than to have one
 * and be logged out of it: the session cookie is long-lived, and the one buyer
 * this is known to have cost had no account at all. Both pages carry the intent
 * across to each other, so the other case costs one click rather than the sale.
 */
export function authUrlForCheckout(
  base: '/signup' | '/login',
  { redirect, productSlug }: { redirect: string; productSlug: string },
): string {
  const params = new URLSearchParams({ redirect })
  if (SLUG.test(productSlug)) params.set(CHECKOUT_PARAM, productSlug)
  return `${base}?${params}`
}

/** Carry both params to the other auth page, so switching does not drop the sale. */
export function crossLink(base: '/signup' | '/login', params: URLSearchParams, redirectTo: string): string {
  const intent = readCheckoutIntent(params)
  if (intent) return authUrlForCheckout(base, { redirect: redirectTo, productSlug: intent })
  return redirectTo === '/account' ? base : `${base}?redirect=${encodeURIComponent(redirectTo)}`
}

export interface ResumeOptions {
  /** Sends the browser to Stripe. Injected so a test never leaves the page. */
  go?: (url: string) => void
  /** Where to land when checkout cannot be started at all. */
  onFallback: (path: string) => void
}

/**
 * Finish the purchase the visitor started before they had an account.
 *
 * Called immediately after signup or login succeeds. Reports the same
 * `cta_checkout_start` the course page reports, tagged with its own location, so
 * a sale recovered here is distinguishable in the funnel from one that never hit
 * the wall. That distinction is the entire point of having built the events.
 *
 * A failure here must never strand somebody who has just created an account and
 * possibly just paid attention for the first time, so it falls back to the page
 * they were headed for and lets the Unlock button there be pressed again.
 */
export async function resumeCheckout(
  productSlug: string,
  redirectTo: string,
  { go = (url) => { window.location.href = url }, onFallback }: ResumeOptions,
): Promise<void> {
  const at = { location: 'auth_resume', courseSlug: productSlug }
  try {
    const { url } = await authApi.checkout(productSlug)
    trackCheckout(EVENT.checkoutStart, at)
    go(url)
  } catch (e) {
    trackCheckout(EVENT.checkoutFailed, { ...at, reason: (e as Error).message || 'resume failed' })
    onFallback(redirectTo)
  }
}
